import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { Button, Input, Select, Textarea, Card } from "@/components/ui";
import { productsApi } from "@/services/api";
import {
  parseCurrency,
  formatCurrency,
  formatCurrencyInput,
} from "@/utils/formatters";

interface ProductFormData {
  name: string;
  description: string;
  detailed_description: string;
  code: string;
  category_id?: string;
  unit: string;
  price: number;
  cost: number;
  billing_type: string;
  is_active: boolean;
}

const BILLING_TYPES = [
  { value: "one_time", label: "Única" },
  { value: "hourly", label: "Por hora" },
  { value: "daily", label: "Diária" },
  { value: "weekly", label: "Semanal" },
  { value: "monthly", label: "Mensal" },
  { value: "quarterly", label: "Trimestral" },
  { value: "semiannual", label: "Semestral" },
  { value: "yearly", label: "Anual" },
  { value: "hourly_billed_daily", label: "Por hora - diária" },
  { value: "hourly_billed_monthly", label: "Por hora - mensal" },
  { value: "daily_billed_monthly", label: "Diária - mensal " },
  { value: "monthly_billed_yearly", label: "Mensal - anualmente" },
  { value: "on_demand", label: "Sob demanda" },
];

const UNITS = [
  { value: "un", label: "Unidade (un)" },
  { value: "hr", label: "Hora (hr)" },
  { value: "m", label: "Metro (m)" },
  { value: "m²", label: "Metro Quadrado (m²)" },
  { value: "kg", label: "Quilograma (kg)" },
  { value: "lt", label: "Litro (lt)" },
  { value: "pç", label: "Peça (pç)" },
  { value: "cx", label: "Caixa (cx)" },
  { value: "serv", label: "Serviço (serv)" },
];

export default function ProductFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isEditing = !!id;

  const [priceDisplay, setPriceDisplay] = useState("R$ 0,00");
  const [costDisplay, setCostDisplay] = useState("R$ 0,00");

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<ProductFormData>({
    defaultValues: {
      name: "",
      description: "",
      detailed_description: "",
      code: "",
      category_id: "",
      unit: "un",
      price: 0,
      cost: 0,
      billing_type: "one_time",
      is_active: true,
    },
  });

  const { data: productData, isLoading: isLoadingProduct } = useQuery({
    queryKey: ["product", id],
    queryFn: () => productsApi.get(id!),
    enabled: isEditing,
  });

  const { data: categoriesData } = useQuery({
    queryKey: ["categories"],
    queryFn: () => productsApi.listCategories(),
  });

  useEffect(() => {
    if (productData?.data) {
      const product = productData.data;
      const priceFormatted = product.price
        ? formatCurrency(product.price)
        : "R$ 0,00";
      const costFormatted = product.cost
        ? formatCurrency(product.cost)
        : "R$ 0,00";

      setPriceDisplay(priceFormatted);
      setCostDisplay(costFormatted);

      reset({
        name: product.name || "",
        description: product.description || "",
        detailed_description: product.detailed_description || "",
        code: product.code || "",
        category_id: product.category_id || "",
        unit: product.unit || "un",
        price: priceFormatted,
        cost: costFormatted,
        billing_type: product.billing_type || "one_time",
        is_active: product.is_active !== false,
      } as any);
    }
  }, [productData, reset]);

  const createMutation = useMutation({
    mutationFn: productsApi.create,
    onSuccess: () => {
      toast.success("Produto criado com sucesso");
      queryClient.invalidateQueries({ queryKey: ["products"] });
      navigate("/admin/products");
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || "Erro ao criar produto");
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: ProductFormData) => productsApi.update(id!, data),
    onSuccess: () => {
      toast.success("Produto atualizado com sucesso");
      queryClient.invalidateQueries({ queryKey: ["products"] });
      queryClient.invalidateQueries({ queryKey: ["product", id] });
      navigate("/admin/products");
    },
    onError: (error: any) => {
      toast.error(
        error?.response?.data?.message || "Erro ao atualizar produto",
      );
    },
  });

  const isLoading = createMutation.isPending || updateMutation.isPending;
  const categories = categoriesData?.data || [];

  const onSubmit = (data: ProductFormData) => {
    const priceValue = parseCurrency(priceDisplay);
    const costValue = parseCurrency(costDisplay);

    if (priceValue <= 0) {
      toast.error("Preço de venda deve ser maior que zero");
      return;
    }

    const payload = {
      ...data,
      category_id: data.category_id || undefined,
      price: priceValue,
      cost: costValue,
    };

    if (isEditing) {
      updateMutation.mutate(payload);
    } else {
      createMutation.mutate(payload);
    }
  };

  // Calcular margem baseado nos valores exibidos
  const price = parseCurrency(priceDisplay);
  const cost = parseCurrency(costDisplay);
  const margin =
    price > 0 && cost > 0 ? (((price - cost) / cost) * 100).toFixed(1) : "0";

  if (isEditing && isLoadingProduct) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate("/admin/products")}
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-3xl font-bold text-text-primary">
            {isEditing ? "Editar Produto" : "Novo Produto"}
          </h1>
          <p className="text-text-secondary mt-1">
            {isEditing
              ? "Atualize as informações do produto"
              : "Cadastre um novo produto ou serviço"}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Basic Info */}
        <Card className="p-6">
          <h2 className="text-lg font-semibold text-text-primary mb-4">
            Informações Básicas
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Nome do Produto *"
              {...register("name", { required: "Nome é obrigatório" })}
              error={errors.name?.message}
            />
            <Input
              label="Código / SKU"
              {...register("code")}
              placeholder="PROD-001"
            />
            <Select
              label="Categoria"
              {...register("category_id")}
              options={[
                { value: "", label: "Sem categoria" },
                ...categories.map((cat: any) => ({
                  value: cat.id,
                  label: cat.name,
                })),
              ]}
            />
            <Select
              label="Unidade de Medida"
              {...register("unit")}
              options={UNITS}
            />
            <div className="md:col-span-2">
              <Textarea
                label="Descrição Curta"
                {...register("description")}
                placeholder="Descrição breve do produto ou serviço..."
                rows={2}
              />
            </div>
          </div>
        </Card>

        {/* Detailed Description */}
        <Card className="p-6">
          <h2 className="text-lg font-semibold text-text-primary mb-2">
            Escopo / Funções / Capacidades
          </h2>
          <p className="text-sm text-text-muted mb-4">
            Descreva detalhadamente o escopo, funções e capacidades deste
            produto/serviço. Esta informação aparecerá em uma página separada no
            PDF do orçamento.
          </p>
          <Textarea
            {...register("detailed_description")}
            placeholder="Ex:\n\n• Desenvolvimento de sistema web responsivo\n• Painel administrativo completo\n• Integração com APIs de pagamento\n• Relatórios gerenciais\n• Suporte técnico por 90 dias..."
            rows={10}
          />
        </Card>

        {/* Pricing */}
        <Card className="p-6">
          <h2 className="text-lg font-semibold text-text-primary mb-4">
            Preços e Cobrança
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Select
              label="Tipo de Cobrança"
              {...register("billing_type")}
              options={BILLING_TYPES}
            />
            <Input
              label="Preço de Custo"
              type="text"
              value={costDisplay}
              onChange={(e) => {
                const formatted = formatCurrencyInput(e.target.value);
                setCostDisplay(formatted);
                setValue("cost", formatted as any);
              }}
              placeholder="R$ 0,00"
            />
            <Input
              label="Preço de Venda *"
              type="text"
              value={priceDisplay}
              onChange={(e) => {
                const formatted = formatCurrencyInput(e.target.value);
                setPriceDisplay(formatted);
                setValue("price", formatted as any);
              }}
              error={errors.price?.message}
              placeholder="R$ 0,00"
            />
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-2">
                Margem
              </label>
              <div className="h-11 px-4 flex items-center bg-dark-lighter border border-white/10 rounded-lg">
                <span
                  className={`font-mono ${Number(margin) >= 0 ? "text-green-400" : "text-red-400"}`}
                >
                  {margin}%
                </span>
              </div>
            </div>
          </div>
        </Card>

        {/* Status */}
        <Card className="p-6">
          <h2 className="text-lg font-semibold text-text-primary mb-4">
            Status
          </h2>
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              {...register("is_active")}
              className="checkbox"
            />
            <div>
              <span className="text-text-primary font-medium">
                Produto ativo
              </span>
              <p className="text-sm text-text-muted">
                Produtos inativos não aparecem na criação de orçamentos
              </p>
            </div>
          </label>
        </Card>

        {/* Actions */}
        <div className="flex items-center justify-end gap-4">
          <Button
            type="button"
            variant="secondary"
            onClick={() => navigate("/admin/products")}
          >
            Cancelar
          </Button>
          <Button type="submit" isLoading={isLoading}>
            {isEditing ? "Salvar Alterações" : "Cadastrar Produto"}
          </Button>
        </div>
      </form>
    </div>
  );
}
